import { useState } from 'react';
import type { JSX } from 'react';
import { Button } from '../ui/button';
import { AddLink } from '../ui/add-link';
import { NewMapForm } from './NewMapForm.js';
import { ArrowDown, ArrowUp, Cast, Columns2, Lock, Map as MapIcon, Play, Share2, Star, StarOff, X } from 'lucide-react';
import { IconButton } from '../ui/icon-button';
import { ListRow, Row } from '../ui/row';
import type { MenuEntry } from '../ui/row-menu';
import { PaneSection } from './PaneParts.js';
import type { TranslationKeys } from '../../i18n/types.js';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import type { MapOp } from './useBeatMaps.js';
import type { ShowAttempt } from './useShowMapToPlayers.js';
import { Input } from '../ui/input';
import { searchFilter } from '../../lib/search/engine';

/** From this many maps to choose from, the choice gets a search field. */
const MAP_SEARCH_FROM = 7;

/** One map of the beat as the host resolved it from the page store. */
export interface BeatMapItem {
  readonly pageId: string;
  /** undefined — not in the store: deleted (stores ready) or not loaded yet. */
  readonly name: string | undefined;
  /** P03: 'here' — only for this beat; 'shared' — in the Library; 'other' — another beat's. */
  readonly owner?: 'here' | 'shared' | 'other';
}

export interface BeatMapsProps {
  /** The beat's confirmed maps, in the server's order. */
  readonly maps: readonly BeatMapItem[];
  /** The confirmed primary (null — none). */
  readonly primaryId: string | null;
  /** The map the players see (confirmed activePageId). */
  readonly activePageId: string;
  /** Campaign maps the GM can add. */
  readonly candidates: readonly { readonly id: string; readonly name: string }[];
  /** Before the stores are hydrated «not in the store» means «not loaded yet», not «deleted». */
  readonly storesReady: boolean;
  /** Operations of THIS beat not confirmed yet. */
  readonly ops: readonly MapOp[];
  readonly showAttempt: ShowAttempt | undefined;
  readonly t: (key: keyof TranslationKeys) => string;
  readonly onAdd: (pageId: string, primaryIfNone: boolean) => void;
  /** «New map» (one window: name + picture): make a map with this name, add it to the beat and give it the picture. */
  readonly onCreate?: (name: string, primaryIfNone: boolean, image: File | undefined) => void;
  readonly onRemove: (pageId: string, nextPrimaryPageId: string | null) => void;
  readonly onSetPrimary: (pageId: string | null) => void;
  readonly onMove: (pageId: string, beforePageId: string | null) => void;
  /** Local only: a tab in the main pane. Never activates anything. */
  readonly onOpen: (pageId: string) => void;
  /** Local only: the second pane. Never activates anything. */
  readonly onOpenBeside: (pageId: string) => void;
  /** Ask to show the map to the players (the host confirms with its name). */
  readonly onAskShow: (pageId: string) => void;
  /** P03: «Только для этого бита» (toBeat) / «В общий список» of a map of this beat. */
  readonly onSetOwner?: (pageId: string, toBeat: boolean) => void;
  readonly onRetry: (key: string) => void;
  readonly onDismiss: (key: string) => void;
}

/**
 * «Maps of the beat» (GM Studio 6): an ordered, explicit set and an optional
 * primary — the default map to open, never shown to players by itself. What
 * is listed is what the server confirmed; pending operations are notes until
 * their answer. Opening a map here or beside is local; only «Show to
 * players…» (after a confirmation naming the map) changes what players see.
 * Removing the primary asks for the next one or none — another map is never
 * chosen silently. A deleted map stays a visible link until removed.
 */
export function BeatMaps({
  maps,
  primaryId,
  activePageId,
  candidates,
  storesReady,
  ops,
  showAttempt,
  t,
  onAdd,
  onCreate,
  onRemove,
  onSetPrimary,
  onMove,
  onOpen,
  onOpenBeside,
  onAskShow,
  onSetOwner,
  onRetry,
  onDismiss,
}: BeatMapsProps): JSX.Element {
  const [picking, setPicking] = useState(false);
  // A long map list gets a search over its choice (group M: the app's one matcher — typos, ё/е, best first).
  const [mapQuery, setMapQuery] = useState('');
  const [creatingMap, setCreatingMap] = useState(false);
  const closePicker = (): void => {
    setPicking(false);
    setMapQuery('');
  };
  // The primary being removed: the GM chooses the next primary or none.
  const [removingPrimary, setRemovingPrimary] = useState<string | undefined>(undefined);
  const [nextPrimary, setNextPrimary] = useState<string>('');

  const ids = maps.map((m) => m.pageId);
  const available = (m: BeatMapItem): boolean => m.name !== undefined;
  const nameOf = (pageId: string): string =>
    maps.find((m) => m.pageId === pageId)?.name ?? candidates.find((c) => c.id === pageId)?.name ?? pageId;
  const opOf = (kind: MapOp['kind'], pageId?: string): MapOp | undefined =>
    ops.find((o) => o.kind === kind && (pageId === undefined || o.pageId === pageId));
  const movePending = opOf('move')?.status === 'pending';
  const primaryPending = opOf('primary')?.status === 'pending';
  const addable = candidates.filter((c) => !ids.includes(c.id));
  const shownAddable = searchFilter(mapQuery, addable, (c) => [c.name]);
  const asPrimary = maps.length === 0 && primaryId === null;
  const primary = primaryId === null ? undefined : maps.find((m) => m.pageId === primaryId);

  const opText = (op: MapOp): string => {
    if (op.kind === 'create') {
      const name = op.name ?? '';
      if (op.status === 'pending') return t('studio.maps.creating').replace('{name}', name);
      if (op.status === 'unknown') return t('studio.maps.createUnknown').replace('{name}', name);
      if (op.status === 'unresolved') return t('studio.maps.createUnresolved').replace('{name}', name);
      const failed = `${t('studio.maps.createFailed')} «${name}»`;
      return op.error !== undefined && op.error !== '' ? `${failed}: ${op.error}` : failed;
    }
    if (op.status === 'offline') return t('studio.maps.offline');
    if (op.status === 'lost') return t('studio.maps.lost');
    if (op.status === 'pending') {
      return t(
        op.kind === 'add' ? 'studio.maps.adding' : op.kind === 'remove' ? 'studio.maps.removing' : op.kind === 'primary' ? 'studio.maps.primaryChanging' : 'studio.maps.moving',
      );
    }
    const title = t(
      op.kind === 'add' ? 'studio.maps.addFailed' : op.kind === 'remove' ? 'studio.maps.removeFailed' : op.kind === 'primary' ? 'studio.maps.primaryFailed' : 'studio.maps.moveFailed',
    );
    return op.error !== undefined && op.error !== '' ? `${title}: ${op.error}` : title;
  };

  const askRemove = (pageId: string): void => {
    if (pageId === primaryId) {
      setNextPrimary('');
      setRemovingPrimary(pageId);
      return;
    }
    onRemove(pageId, null);
  };

  return (
    <PaneSection
      title={t('studio.maps.title')}
      testid="beat-maps"
      actions={
        primary !== undefined && available(primary) ? (
          <IconButton
            label={t('studio.maps.openPrimary')}
            icon={<Star size={14} aria-hidden="true" />}
            data-testid="beat-maps-open-primary"
            onClick={(): void => onOpen(primary.pageId)}
          />
        ) : undefined
      }
    >
      {maps.length === 0 ? (
        <p className="mb-2 text-meta leading-relaxed text-dim" data-testid="beat-maps-empty">
          {t('studio.maps.empty')}
        </p>
      ) : primaryId === null ? (
        <p className="mb-1 text-meta text-dim" data-testid="beat-maps-no-primary">
          {t('studio.maps.noPrimary')}
        </p>
      ) : primary !== undefined && !available(primary) && storesReady ? (
        <p className="mb-1 text-meta text-dim" data-testid="beat-maps-primary-deleted">
          {t('studio.maps.primaryDeleted')}
        </p>
      ) : null}

      {/* One 28 px line per map (group T): the name opens it, «основная» / «у игроков» are read as words, «Рядом» is
          the row's icon, every other action is in ⋯ in words (they were a row of text links under the name). */}
      <ul className="mb-1 flex flex-col">
        {maps.map((m, i) => {
          const isPrimary = m.pageId === primaryId;
          const playersSee = m.pageId === activePageId;
          const removing = opOf('remove', m.pageId);
          const busy = removing !== undefined && removing.status !== 'failed';
          const own = showAttempt !== undefined && showAttempt.pageId === m.pageId ? showAttempt : undefined;
          const ok = available(m);
          const menu: MenuEntry[] = [
            { label: t('studio.maps.open'), icon: MapIcon, hidden: !ok, testid: 'beat-map-open', onSelect: () => onOpen(m.pageId) },
            { label: t('studio.maps.besideLong'), icon: Columns2, hidden: !ok, testid: 'beat-map-beside-item', onSelect: () => onOpenBeside(m.pageId) },
            {
              label: own?.pending === true ? t('studio.maps.showing') : t('studio.maps.show'),
              icon: Cast,
              hidden: !ok || playersSee,
              disabled: showAttempt?.pending === true,
              testid: 'beat-map-show',
              onSelect: () => onAskShow(m.pageId),
            },
            { separator: true },
            isPrimary
              ? { label: t('studio.maps.unsetPrimary'), icon: StarOff, hidden: !ok, disabled: primaryPending, testid: 'beat-map-unset-primary', onSelect: () => onSetPrimary(null) }
              : { label: t('studio.maps.makePrimary'), icon: Star, hidden: !ok, disabled: primaryPending, testid: 'beat-map-make-primary', onSelect: () => onSetPrimary(m.pageId) },
            {
              label: t(m.owner === 'here' ? 'studio.owner.toShared' : 'studio.owner.mapToBeat'),
              icon: m.owner === 'here' ? Share2 : Lock,
              hidden: !ok || onSetOwner === undefined || m.owner === 'other',
              testid: 'beat-map-owner',
              onSelect: () => onSetOwner?.(m.pageId, m.owner !== 'here'),
            },
            { label: t('studio.maps.up'), icon: ArrowUp, hidden: !ok, disabled: i === 0 || movePending, testid: 'beat-map-up', onSelect: () => onMove(m.pageId, ids[i - 1] ?? null) },
            { label: t('studio.maps.down'), icon: ArrowDown, hidden: !ok, disabled: i === maps.length - 1 || movePending, testid: 'beat-map-down', onSelect: () => onMove(m.pageId, ids[i + 2] ?? null) },
            { separator: true },
            { label: t('studio.maps.remove'), icon: X, disabled: !ok && !storesReady, testid: 'beat-map-remove', onSelect: () => askRemove(m.pageId) },
          ];
          return (
            <ListRow
              key={m.pageId}
              data-testid="beat-map-row"
              data-page-id={m.pageId}
              data-primary={isPrimary ? 'true' : 'false'}
              data-owner={m.owner ?? 'shared'}
              label={
                ok ? (
                  m.name
                ) : (
                  <span className="text-dim" data-testid={storesReady ? 'beat-map-deleted' : 'beat-map-loading'}>
                    {storesReady ? t('studio.maps.deleted') : '…'}
                  </span>
                )
              }
              tip={ok ? m.name : undefined}
              onOpen={ok ? (): void => onOpen(m.pageId) : undefined}
              labelTestid="beat-map-name"
              pills={
                <>
                  {isPrimary ? (
                    <span className="flex flex-none items-center gap-1 text-pill text-hl" data-testid="beat-map-primary-badge">
                      <Star size={12} aria-hidden="true" />
                      {t('studio.maps.primary')}
                    </span>
                  ) : null}
                  {ok && playersSee ? (
                    <span className="flex flex-none items-center gap-1 text-pill text-ok" data-testid="beat-map-players-see">
                      <Play size={12} aria-hidden="true" />
                      {t('studio.maps.playersSee')}
                    </span>
                  ) : null}
                </>
              }
              meta={busy ? <span data-testid="beat-map-row-status">{opText(removing)}</span> : undefined}
              actions={
                busy || !ok ? undefined : (
                  <IconButton
                    size="sm"
                    label={t('studio.maps.besideLong')}
                    icon={<Columns2 size={14} aria-hidden="true" />}
                    data-testid="beat-map-beside"
                    onClick={(): void => onOpenBeside(m.pageId)}
                  />
                )
              }
              menu={busy ? undefined : menu}
              menuTestid="beat-map-menu"
              after={
                own !== undefined && !own.pending ? (
                  <li className="list-none px-1.5 pb-1 text-meta text-danger" data-testid="beat-map-show-note">
                    {own.note}
                  </li>
                ) : undefined
              }
            />
          );
        })}
        {ops
          .filter((op) => op.status !== 'pending' || op.kind === 'add' || op.kind === 'primary' || op.kind === 'move' || op.kind === 'create')
          .filter((op) => !(op.kind === 'remove' && op.status === 'offline'))
          .map((op) => (
            <li
              key={op.key}
              className={`flex min-h-7 min-w-0 items-center gap-2 rounded px-1.5 text-meta ${
                op.status === 'failed' ? 'text-danger' : 'text-dim'
              }`}
              data-testid="beat-map-op"
              data-kind={op.kind}
              data-status={op.status}
            >
              {op.pageId !== '' ? <span className="min-w-0 truncate text-fg">{nameOf(op.pageId)}</span> : null}
              <span className="min-w-0 flex-1">{opText(op)}</span>
              {op.status === 'failed' && op.final !== true ? (
                <Button type="button" size="sm" variant="danger" className="flex-none" data-testid="beat-map-retry" onClick={(): void => onRetry(op.key)}>
                  {t('studio.maps.retry')}
                </Button>
              ) : null}
              {op.status === 'failed' || op.status === 'lost' || op.status === 'unresolved' ? (
                <Button type="button" variant="ghost" size="inline" className="flex-none px-1 text-muted-foreground" data-testid="beat-map-dismiss" onClick={(): void => onDismiss(op.key)}>
                  {t('studio.maps.dismiss')}
                </Button>
              ) : null}
            </li>
          ))}
      </ul>

      {/* A campaign map into the beat: «＋ Карта кампании…» opens the list with a search (it was a native select and
          «Добавить»); one click on a map adds it — the first one becomes the primary, said in the list's title. */}
      {picking ? (
        <div className="mb-1 flex flex-col gap-1.5 rounded-md border border-line bg-panel-2 px-2 py-1.5" data-testid="beat-maps-picker">
          <div className="flex h-7 items-center gap-2">
            <span className="min-w-0 flex-1 truncate text-meta font-semibold text-fg" data-testid="beat-maps-picker-title">
              {t(asPrimary ? 'studio.maps.addAsPrimary' : 'studio.maps.addTitle')}
            </span>
            <IconButton size="sm" label={t('studio.materials.close')} icon={<X size={14} aria-hidden="true" />} data-testid="beat-maps-picker-close" onClick={closePicker} />
          </div>
          {addable.length > MAP_SEARCH_FROM ? (
            <Input
              type="search"
              autoFocus
              data-testid="beat-maps-find"
              aria-label={t('search.maps.find')}
              placeholder={t('search.maps.find')}
              value={mapQuery}
              onChange={(e): void => setMapQuery(e.target.value)}
            />
          ) : null}
          <ul className="flex max-h-56 flex-col overflow-y-auto">
            {shownAddable.length === 0 ? <li className="px-1.5 py-1 text-meta text-dim">{t('studio.materials.nothingFound')}</li> : null}
            {shownAddable.map((c) => (
              <li key={c.id} className="list-none">
                <Row
                  as="button"
                  type="button"
                  title={c.name}
                  data-testid={`beat-maps-candidate-${c.id}`}
                  onClick={(): void => {
                    onAdd(c.id, asPrimary);
                    closePicker();
                  }}
                >
                  <MapIcon size={12} className="flex-none text-dim" aria-hidden="true" />
                  <span className="min-w-0 truncate">{c.name}</span>
                </Row>
              </li>
            ))}
          </ul>
        </div>
      ) : addable.length > 0 ? (
        <AddLink plus={false} data-testid="beat-maps-add" onClick={(): void => setPicking(true)}>
          {t('studio.maps.addCampaignMap')}
        </AddLink>
      ) : maps.length > 0 ? (
        <p className="text-meta text-dim" data-testid="beat-maps-all-in">
          {t('studio.maps.allIn')}
        </p>
      ) : null}

      {onCreate !== undefined ? (
        creatingMap ? (
          <NewMapForm
            t={t}
            onSubmit={(name, image): void => {
              setCreatingMap(false);
              onCreate(name, asPrimary, image);
            }}
            onCancel={(): void => setCreatingMap(false)}
          />
        ) : (
          <AddLink plus={false} data-testid="beat-maps-create" onClick={(): void => setCreatingMap(true)}>
            {t('studio.maps.create')}
          </AddLink>
        )
      ) : null}

      <AlertDialog
        open={removingPrimary !== undefined}
        onOpenChange={(open): void => {
          if (!open) setRemovingPrimary(undefined);
        }}
      >
        <AlertDialogContent data-testid="beat-map-remove-primary-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('studio.maps.removePrimaryTitle').replace('{name}', removingPrimary === undefined ? '' : maps.find((m) => m.pageId === removingPrimary)?.name ?? t('studio.maps.deleted'))}
            </AlertDialogTitle>
            <AlertDialogDescription>{t('studio.maps.removePrimaryBody')}</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex flex-col gap-1.5 text-ui" role="radiogroup" aria-label={t('studio.maps.removePrimaryBody')}>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="beat-map-next-primary"
                data-testid="beat-map-next-primary-none"
                checked={nextPrimary === ''}
                onChange={(): void => setNextPrimary('')}
              />
              {t('studio.maps.keepNoPrimary')}
            </label>
            {maps
              .filter((m) => m.pageId !== removingPrimary && available(m))
              .map((m) => (
                <label key={m.pageId} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="beat-map-next-primary"
                    data-testid={`beat-map-next-primary-${m.pageId}`}
                    checked={nextPrimary === m.pageId}
                    onChange={(): void => setNextPrimary(m.pageId)}
                  />
                  {t('studio.maps.makeNextPrimary').replace('{name}', m.name ?? m.pageId)}
                </label>
              ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="beat-map-remove-primary-cancel" onClick={(): void => setRemovingPrimary(undefined)}>
              {t('studio.maps.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="beat-map-remove-primary-confirm"
              onClick={(): void => {
                const pageId = removingPrimary;
                setRemovingPrimary(undefined);
                if (pageId !== undefined) onRemove(pageId, nextPrimary === '' ? null : nextPrimary);
              }}
            >
              {t('studio.maps.removeConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PaneSection>
  );
}

export default BeatMaps;
